import * as React from "react";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import styled from "styled-components";
import { IfPermitted, PaginatedList, Spinner } from "components/common";
import ReplaySearchButton from "views/components/widgets/ReplaySearchButton";
import { fetchPaginatedInvestigationSecurityEvents } from "security-app/hooks/api/investigationsAPI";
import { investigationDrawerEventsQueryKey } from "security-app/hooks/queryKeys";
import { defaultOnError } from "util/conditional/onError";
import useRightSidebar from "hooks/useRightSidebar";
import { Row, EvidenceList, EvidenceRow, StyledLink } from "../styled.components";
const DEFAULT_EVENTS_PAGE_SIZE = 10;
const getTimeRange = (replayInfo) => {
  if (!replayInfo) return null;
  return {
    type: "absolute",
    from: replayInfo.timerange_start,
    to: replayInfo.timerange_end
  };
};
const TitleRow = styled(Row)`
  overflow: hidden;
`;
const useInvestigationEvents = (investigationId, page) => {
  var _a, _b, _c;
  const { data: paginatedEvents, isLoading } = useQuery({
    queryKey: investigationDrawerEventsQueryKey(investigationId, page),
    queryFn: () => defaultOnError(
      fetchPaginatedInvestigationSecurityEvents(investigationId, page, DEFAULT_EVENTS_PAGE_SIZE),
      "Error fetching investigation events"
    ),
    enabled: !!investigationId,
    placeholderData: keepPreviousData
  });
  const events = ((_a = paginatedEvents == null ? void 0 : paginatedEvents.elements) != null ? _a : []).map((securityEvent) => ({
    ...securityEvent.event,
    id: securityEvent.event_id
  }));
  const total = (_c = (_b = paginatedEvents == null ? void 0 : paginatedEvents.pagination) == null ? void 0 : _b.total) != null ? _c : 0;
  const count = events.length;
  return {
    count,
    events,
    isLoading,
    total
  };
};
const Events = ({ investigation }) => {
  const { openSidebar } = useRightSidebar();
  const [page, setPage] = useState(1);
  const { events, isLoading: loadingEvents, total: totalEvents } = useInvestigationEvents(investigation == null ? void 0 : investigation.id, page);
  const onPageChange = (newPage) => {
    setPage(newPage);
  };
  if (loadingEvents) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading events ..." });
  }
  if (totalEvents === 0) {
    return /* @__PURE__ */ React.createElement("div", { style: { color: "gray" } }, "There are no events to list");
  }
  return /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      activePage: page,
      pageSize: DEFAULT_EVENTS_PAGE_SIZE,
      onChange: onPageChange,
      totalItems: totalEvents,
      showPageSizeSelect: false,
      hideFirstAndLastPageLinks: true,
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(EvidenceList, null, events.map((event) => /* @__PURE__ */ React.createElement(EvidenceRow, { key: event.id, $fullWidth: true, $gap: "0.5rem" }, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `eventdefinitions:read:${event.event_definition_id}` }, event.replay_info && /* @__PURE__ */ React.createElement(
      ReplaySearchButton,
      {
        queryString: event.replay_info.query,
        timerange: getTimeRange(event.replay_info),
        streams: event.replay_info.streams
      }
    )), /* @__PURE__ */ React.createElement(TitleRow, { $gap: "0" }, /* @__PURE__ */ React.createElement(
      StyledLink,
      {
        onClick: () => openSidebar({
          id: `event-${event.id}`,
          title: "Event",
          componentKey: "EventReplaySideBarDetails",
          props: { alertId: event.id }
        }),
        title: event.message
      },
      event.message
    )))))
  );
};
export default Events;
